import React, { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux';
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import Box from '../../../components/Box';
import ColumnFilterChips from '../../../components/ColumnFilter/ColumnFilterChips';
import ColumnFilterRow from '../../../components/ColumnFilter/ColumnFilterRow';
import { ColumnFilterDefinitions } from '../../../components/ColumnFilter/ColumnFilterTypes';
import CustomDatePicker from '../../../components/CustomDatePicker';
import CustomNumberInput from '../../../components/CustomNumberInput';
import CustomSwitch from '../../../components/CustomSwitch';
import CustomTextInput from '../../../components/CustomTextInput';
import TableFooter from '../../../components/TableFooter';
import { Helpers } from '../../../helpers/Helpers';
import { useSortableColumns } from '../../../helpers/useSortableColumns';
import { ColumnChooserModel } from '../../../models/common/ColumnChooserModel';
import { ColumnFilterModel } from '../../../models/common/ColumnFilterModel';
import { ApplyEscalationRequest, GetBulkPriceAgreementListRequest, initialGetBulkPriceAgreementListRequest } from '../../../models/request/bulkPrice/BulkPriceRequests';
import {
    ApplyEscalationResponse,
    BulkPriceAgreementItem,
    BulkPriceSchemeItem,
    EscalationCostItemWeight,
    GetEscalationCostItemWeightsResponse,
    GetPriceEscalationHistoryResponse,
    PriceEscalationHistoryItem,
    ExportBulkPriceResponse,
    GetBulkPriceAgreementListResponse,
    GetBulkPriceAgreementListResponseData,
    initialGetBulkPriceAgreementListResponseData
} from '../../../models/response/bulkPrice/BulkPriceResponses';
import { storeActivityIndicator } from '../../../redux/reducers/authSlice';
import { BulkPriceService } from '../../../services/BulkPriceService';
import { KTCard } from '../../../_metronic/helpers/components/KTCard';
import { KTCardBody } from '../../../_metronic/helpers/components/KTCardBody';
import Content from '../../../_metronic/layout/components/content/Content';
import { PageTitle } from '../../../_metronic/layout/core';
import { ToolbarWrapper } from '../../../_metronic/layout/components/toolbar';
import { secimiOku } from '../bulkPriceSecim';

/** Şablondan gelen kriter kolonlarının adı bu önekle taşınır. */
const DYNAMIC_PREFIX = 'dynamic ';

const BulkPriceDetail = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const pageName = "Ayarlar > Toplu Fiyat Güncelleme"

    const [sablonlar] = useState<BulkPriceSchemeItem[]>(secimiOku());
    const tekSablon = sablonlar.length === 1 ? sablonlar[0] : undefined;

    const [data, setData] = useState<GetBulkPriceAgreementListResponseData>(initialGetBulkPriceAgreementListResponseData);
    const [filter, setFilter] = useState<GetBulkPriceAgreementListRequest>({
        ...initialGetBulkPriceAgreementListRequest,
        schemes: sablonlar.map(x => ({ moduleKey: x.moduleKey, schemeId: x.schemeId }))
    });

    // ---------------- eskalasyon alanları ----------------
    const [startDate, setStartDate] = useState<string | undefined>(undefined);
    const [endDate, setEndDate] = useState<string | undefined>(undefined);
    const [rate, setRate] = useState<string | undefined>(undefined);
    const [amount, setAmount] = useState<string | undefined>(undefined);
    const [escalationDescription, setEscalationDescription] = useState<string | undefined>(undefined);

    /** "Tüm Maliyet Kalemlerine": açıkken genel oran/tutar, kapalıyken kalem bazlı giriş. */
    const [allCostItems, setAllCostItems] = useState<boolean>(true);

    /** Kalem bazlı giriş satırları. Ağırlık Eskalasyon Dağılımından gelir, değiştirilemez. */
    const [costItems, setCostItems] = useState<EscalationCostItemWeight[]>([]);
    const [costItemSource, setCostItemSource] = useState<string | undefined>(undefined);

    const [history, setHistory] = useState<PriceEscalationHistoryItem[]>([]);
    const [historyOpen, setHistoryOpen] = useState(false);

    const buYil = new Date().getFullYear();

    // Oran girilmişse tutar, tutar girilmişse oran kapalı: ikisi birden uygulanmaz.
    const oranDolu = !Helpers.isEmpty(rate);
    const tutarDolu = !Helpers.isEmpty(amount);

    const basYili = Helpers.isEmpty(startDate) ? undefined : new Date(startDate + '').getFullYear();
    const basGecerli = !Helpers.isEmpty(startDate) && basYili !== undefined && basYili >= buYil;
    const bitGecerli = !Helpers.isEmpty(endDate) && (Helpers.isEmpty(startDate) || (endDate + '') >= (startDate + ''));
    // Kalem bazlı girişte her satırda oran YA DA tutar olur; ikisi birden olamaz.
    const kalemHatasi = costItems.some(x => !Helpers.isEmpty(x.rate) && !Helpers.isEmpty(x.amount));
    const kalemDolu = costItems.some(x => Helpers.toNumber(x.rate) != 0 || Helpers.toNumber(x.amount) != 0);

    const degerGecerli = allCostItems ? (oranDolu || tutarDolu) : (kalemDolu && !kalemHatasi);
    const eskalasyonGecerli = basGecerli && bitGecerli && degerGecerli;

    /** Kalem bazlı oranların fiyattan bağımsız toplam etkisi: toplam(ağırlık% x artış%). */
    const etkinOran = costItems.reduce((t, x) => t + ((x.weight ?? 0) * Helpers.toNumber(x.rate) / 100), 0);
    const kalemTutarToplami = costItems.reduce((t, x) => t + Helpers.toNumber(x.amount), 0);

    useEffect(() => {
        if (sablonlar.length === 0)
            return;

        getData();
        getCostItemWeights();
        getHistory();
    }, [])

    /** Kalem bazlı giriş gridi: en son Eskalasyon Dağılımı kaydının ağırlıkları. */
    const getCostItemWeights = async () => {
        try {
            var response: GetEscalationCostItemWeightsResponse = await BulkPriceService.getCostItemWeights();

            if (!response.isSuccess)
                return;

            setCostItems((response.data.items ?? []).map(x => ({ ...x, rate: undefined, amount: undefined })));
            setCostItemSource(response.data.sourceInfo);
        } catch {
            // Ağırlıklar gelmezse kalem bazlı giriş kullanılamaz; anahtar açık kalır.
        }
    }

    const getHistory = async () => {
        try {
            var response: GetPriceEscalationHistoryResponse = await BulkPriceService.getEscalationHistory({
                schemes: sablonlar.map(x => ({ moduleKey: x.moduleKey, schemeId: x.schemeId })),
                skip: 0,
                take: 20
            });

            if (response.isSuccess)
                setHistory(response.data.items ?? []);
        } catch {
            // Geçmiş gelmezse ekranın geri kalanı çalışmaya devam eder.
        }
    }

    const setKalemDegeri = (costItemId: string, alan: 'rate' | 'amount', deger: string | undefined) => {
        setCostItems(costItems.map(function (x) {
            if (x.costItemId != costItemId)
                return x;

            // Aynı satırda oran ve tutar birlikte olamaz: biri yazılınca diğeri temizlenir.
            return alan == 'rate'
                ? { ...x, rate: deger, amount: Helpers.isEmpty(deger) ? x.amount : undefined }
                : { ...x, amount: deger, rate: Helpers.isEmpty(deger) ? x.rate : undefined };
        }));
    }

    const applyEscalation = async () => {
        if (!eskalasyonGecerli)
            return;

        try {
            dispatch(storeActivityIndicator(true));

            var obj: ApplyEscalationRequest = {
                schemes: sablonlar.map(x => ({ moduleKey: x.moduleKey, schemeId: x.schemeId })),
                agreementIds: secililer.length > 0 ? secililer.map(x => x.agreementId) : undefined,
                startDate: startDate,
                endDate: endDate,
                allCostItems: allCostItems,
                rate: allCostItems ? rate : undefined,
                amount: allCostItems ? amount : undefined,
                description: escalationDescription,
                items: allCostItems ? [] : costItems
                    .filter(x => !Helpers.isEmpty(x.rate) || !Helpers.isEmpty(x.amount))
                    .map(x => ({ costItemId: x.costItemId, rate: x.rate, amount: x.amount })),
            };

            var response: ApplyEscalationResponse = await BulkPriceService.applyEscalation(obj);

            if (!response.isSuccess) {
                toast.error(response.message);
                return;
            }

            toast.success(response.message);

            // İş kuyrukta çalışacak; geçmiş ve liste biraz sonra güncellenir.
            getHistory();
        } catch (error: any) {
            toast.error(error);
        }
        finally {
            dispatch(storeActivityIndicator(false));
        }
    }

    const performPagination = (i: number) => {
        getData({ ...filter, currentPage: i, skip: (i - 1) * filter.take });
    }

    const changeTake = (_take: number) => {
        getData({ ...filter, currentPage: 1, skip: 0, take: _take });
    }

    const lastRequestNo = useRef(0);

    const getData = async (_filter: GetBulkPriceAgreementListRequest | undefined = undefined) => {
        const requestNo = ++lastRequestNo.current;

        try {
            if (_filter)
                setFilter(_filter);
            else
                _filter = filter;

            dispatch(storeActivityIndicator(true))

            var response: GetBulkPriceAgreementListResponse = await BulkPriceService.getAgreementList(_filter);

            if (requestNo != lastRequestNo.current)
                return;

            if (!response.isSuccess) {
                toast.error(response.message)
                return;
            }

            setData(response.data);
        } catch (error: any) {
            toast.error(error)
        }
        finally {
            if (requestNo == lastRequestNo.current)
                dispatch(storeActivityIndicator(false))
        }
    }

    const setItemSelected = (_id: string) => {
        setData({
            ...data,
            items: data.items.map(function (x) {
                if (x.agreementId == _id)
                    x.isSelected = !x.isSelected;
                return x;
            })
        })
    }

    const selectAll = () => {
        var isAllSelected = data.items.length == data.items.filter(x => x.isSelected).length;

        setData({
            ...data,
            items: data.items.map(function (x) {
                x.isSelected = !isAllSelected;
                return x;
            })
        })
    }

    const secililer = data.items.filter(x => x.isSelected);

    // ---------------- kolonlar ----------------
    // Sabit kolonlar + şablondan gelen kriterler. Çok şablon seçiliyse hangi
    // satırın hangi şablona ait olduğu görünsün diye Şablon kolonu da eklenir.
    const kolonlar: ColumnChooserModel[] = [
        ...(sablonlar.length > 1 ? [{ title: 'Şablon', name: 'schemeRefNo', order: 1, isVisible: true }] : []),
        { title: 'Ref No', name: 'refNo', order: 2, isVisible: true },
        { title: 'Başlangıç T.', name: 'startDateStr', order: 3, isVisible: true },
        { title: 'Bitiş T.', name: 'endDateStr', order: 4, isVisible: true },
        { title: 'Min Fiyat', name: 'minPrice', order: 5, isVisible: true, className: 'custom-text-right' },
        { title: 'Birim Fiyat', name: 'unitPrice', order: 6, isVisible: true, className: 'custom-text-right' },
        ...data.columns.map(function (ad, i): ColumnChooserModel {
            return { title: ad, name: DYNAMIC_PREFIX + ad, order: 7 + i, isVisible: true };
        })
    ];

    const columnFilterDefinitions: ColumnFilterDefinitions = {
        schemeRefNo: { type: 'text' },
        refNo: { type: 'text' },
        startDateStr: { type: 'date' },
        endDateStr: { type: 'date' },
        minPrice: { type: 'number' },
        unitPrice: { type: 'number' },
    };

    data.columns.forEach(function (ad) {
        columnFilterDefinitions[DYNAMIC_PREFIX + ad] = { type: 'text' };
    });

    const sortableColumns = kolonlar.map(x => x.name);
    const { thProps, sortArrow } = useSortableColumns(sortableColumns, filter, getData);

    // Sunucu kriter kolonunu önekiz bekliyor; önek yalnız ekranda kullanılıyor.
    const changeColumnFilters = (columnFilters: ColumnFilterModel[]) => {
        getData({
            ...filter,
            columnFilters: columnFilters.map(x => ({ ...x, column: (x.column ?? '').startsWith(DYNAMIC_PREFIX) ? (x.column ?? '').substring(DYNAMIC_PREFIX.length) : x.column })),
            currentPage: 1,
            skip: 0
        });
    }

    const getColumnValue = (item: BulkPriceAgreementItem, column: string, className: string | undefined) => {
        const tikla = { onClick: () => setItemSelected(item.agreementId) };

        if (column.startsWith(DYNAMIC_PREFIX)) {
            var kriter = (item.criteria ?? []).find(x => x.name == column.substring(DYNAMIC_PREFIX.length));
            return <td className={className} {...tikla}>{kriter ? kriter.value : ''}</td>;
        }

        if (column == 'minPrice' || column == 'unitPrice') {
            var item3: any = item;
            return <td className={className} {...tikla}>{Helpers.correctPrice(item3[column], 2)}</td>;
        }

        var item2: any = item;
        return <td className={className} {...tikla}>{item2[column]}</td>;
    }

    const excelExport = async (mod: 'selected' | 'all' | 'active') => {
        try {
            dispatch(storeActivityIndicator(true))

            var istek: GetBulkPriceAgreementListRequest = {
                ...filter,
                skip: undefined as any,
                take: undefined as any,
                ids: mod == 'selected' ? secililer.map(x => x.agreementId) : undefined,
                onlyActive: mod == 'active',
                columnChooser: kolonlar,
            };

            var response: ExportBulkPriceResponse = await BulkPriceService.exportAgreementList(istek);

            if (!response.isSuccess) {
                toast.error(response.message)
                return;
            }

            window.location.href = Helpers.getEndpoint() + "/Excels/" + response.data;
        } catch (error: any) {
            toast.error(error)
        }
        finally {
            dispatch(storeActivityIndicator(false))
        }
    }

    if (sablonlar.length === 0) {
        return (
            <React.Fragment>
                <ToolbarWrapper filters={undefined} />
                <PageTitle breadcrumbs={[{ title: 'Ayarlar > ', isActive: false, path: '' }]}>Toplu Fiyat Güncelleme</PageTitle>
                <Content>
                    <KTCard className='ebbe-card'>
                        <KTCardBody>
                            <div className='text-center text-muted py-10'>
                                Şablon seçilmemiş. Listeye dönüp en az bir şablon seçin.
                                <div className='mt-5'>
                                    <button className='btn btn-sm btn-primary' onClick={() => navigate('/bulkprice/list')}>Listeye Dön</button>
                                </div>
                            </div>
                        </KTCardBody>
                    </KTCard>
                </Content>
            </React.Fragment>
        )
    }

    return (
        <React.Fragment>
            <ToolbarWrapper filters={
                <div className='m-0'>
                    <a onClick={() => navigate('/bulkprice/list')} className='btn btn-sm fw-bold btn-light'>Listeye Dön</a>
                </div>
            } />

            <PageTitle breadcrumbs={[{ title: 'Ayarlar > ', isActive: false, path: '' }]}>Toplu Fiyat Güncelleme</PageTitle>

            <Content>
                {
                    // Tek şablon seçiliyse şablon bilgileri en üstte, pasif olarak gösterilir.
                    // Çok şablon seçiliyse her şablonun bilgisi farklı olduğu için bu blok
                    // gösterilmez; doğrudan eskalasyon gelir, seçilenler aşağıda rozet olarak durur.
                    tekSablon && (
                        <KTCard className='ebbe-card mb-5'>
                            <KTCardBody>
                                <h5 className='mb-5 text-black'>Şablon Bilgileri</h5>

                                <div className='row'>
                                    <div className='col-md-3'><CustomTextInput isDisabled labelText='Modül' value={tekSablon.moduleName} /></div>
                                    <div className='col-md-3'><CustomTextInput isDisabled labelText='Kod' value={tekSablon.refNo} /></div>
                                    <div className='col-md-6'><CustomTextInput isDisabled labelText='Ad' value={tekSablon.name} /></div>
                                </div>

                                <div className='row mt-5'>
                                    <div className='col-md-3'><CustomTextInput isDisabled labelText='Anlaşma Türü' value={tekSablon.agreementTypeName} /></div>
                                    <div className='col-md-3'><CustomTextInput isDisabled labelText='Anlaşma Tipi' value={tekSablon.agreementKindName} /></div>
                                    <div className='col-md-3'><CustomTextInput isDisabled labelText='Hizmet Kalemi' value={tekSablon.serviceItemName} /></div>
                                    <div className='col-md-3'><CustomTextInput isDisabled labelText='Aktif' value={tekSablon.isCustomActiveName} /></div>
                                </div>

                                <div className='row mt-5'>
                                    <div className='col-md-12'><CustomTextInput isDisabled labelText='Açıklama' value={tekSablon.description} /></div>
                                </div>
                            </KTCardBody>
                        </KTCard>
                    )
                }

                <KTCard className='ebbe-card mb-5'>
                    <KTCardBody>
                        <h5 className='mb-5 text-black'>Eskalasyon Bilgileri</h5>

                        <div className='row'>
                            <div className='col-md-3'>
                                <CustomDatePicker isLabelRequired labelText='Başlangıç Tarihi' value={startDate} setValue={setStartDate} />
                                {
                                    !Helpers.isEmpty(startDate) && !basGecerli && (
                                        <div className='text-danger' style={{ fontSize: 11.5, marginTop: 4 }}>
                                            {buYil}'dan eski bir tarih giremezsiniz.
                                        </div>
                                    )
                                }
                            </div>

                            <div className='col-md-3'>
                                <CustomDatePicker isLabelRequired labelText='Bitiş Tarihi' value={endDate} setValue={setEndDate} />
                                {
                                    !Helpers.isEmpty(endDate) && !bitGecerli && (
                                        <div className='text-danger' style={{ fontSize: 11.5, marginTop: 4 }}>
                                            Bitiş tarihi başlangıçtan önce olamaz.
                                        </div>
                                    )
                                }
                            </div>

                            <div className='col-md-3'>
                                <CustomSwitch
                                    labelText='Tüm Maliyet Kalemlerine'
                                    isActive={allCostItems}
                                    setIsActive={setAllCostItems}
                                />
                                <div className='text-muted' style={{ fontSize: 11.5, marginTop: 4 }}>
                                    {allCostItems ? 'Girilen değer bütün fiyatlara doğrudan uygulanır.' : 'Maliyet kalemi bazında giriş yapılır.'}
                                </div>
                            </div>
                        </div>

                        {
                            allCostItems && (
                                <div className='row mt-5'>
                                    <div className='col-md-3'>
                                        <CustomNumberInput labelText='Uygulanacak Oran (%)' value={rate} setValue={setRate} isDisabled={tutarDolu} />
                                        {
                                            tutarDolu && <div className='text-muted' style={{ fontSize: 11.5, marginTop: 4 }}>Tutar girildiği için kapalı.</div>
                                        }
                                    </div>

                                    <div className='col-md-3'>
                                        <CustomNumberInput labelText='Uygulanacak Tutar' value={amount} setValue={setAmount} isDisabled={oranDolu} />
                                        {
                                            oranDolu
                                                ? <div className='text-muted' style={{ fontSize: 11.5, marginTop: 4 }}>Oran girildiği için kapalı.</div>
                                                : (!degerGecerli && <div className='text-muted' style={{ fontSize: 11.5, marginTop: 4 }}>Oran ya da tutardan birini doldurun.</div>)
                                        }
                                    </div>
                                </div>
                            )
                        }

                        {
                            !allCostItems && (
                                <div className='row mt-5'>
                                    <div className='col-md-12'>
                                        <div className='d-flex justify-content-between align-items-center mb-2 flex-wrap'>
                                            <h6 className='m-0 text-black'>Maliyet Kalemleri</h6>
                                            <span className='text-muted' style={{ fontSize: 12 }}>
                                                {costItemSource ? 'Ağırlıklar: ' + costItemSource : ''}
                                            </span>
                                        </div>

                                        {
                                            costItems.length == 0
                                                ? (
                                                    <div className='text-danger' style={{ fontSize: 12.5 }}>
                                                        Eskalasyon Dağılımı tanımlı değil. Önce Ayarlar &gt; Eskalasyon Dağılımı ekranından
                                                        maliyet kalemlerini tanımlayın.
                                                    </div>
                                                )
                                                : (
                                                    <div className='table-responsive'>
                                                        <table id='kt_table_users' className='table align-middle table-row-dashed dataTable no-footer'>
                                                            <thead>
                                                                <tr className='text-start text-muted fw-bolder text-uppercase'>
                                                                    <th>Maliyet Kalemi</th>
                                                                    <th className='custom-text-right' style={{ width: 170 }}>Eskalasyon Ağırlığı</th>
                                                                    <th style={{ width: 200 }}>Oran (%)</th>
                                                                    <th style={{ width: 200 }}>Tutar</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody className='text-gray-600 fw-bold'>
                                                                {
                                                                    costItems.map((item) => (
                                                                        <tr key={item.costItemId}>
                                                                            <td>{item.name}</td>
                                                                            <td className='custom-text-right'>{Helpers.correctPrice(item.weight, 2)}</td>
                                                                            <td>
                                                                                <CustomNumberInput
                                                                                    value={item.rate}
                                                                                    setValue={(v: any) => setKalemDegeri(item.costItemId, 'rate', v)}
                                                                                    isDisabled={!Helpers.isEmpty(item.amount)}
                                                                                />
                                                                            </td>
                                                                            <td>
                                                                                <CustomNumberInput
                                                                                    value={item.amount}
                                                                                    setValue={(v: any) => setKalemDegeri(item.costItemId, 'amount', v)}
                                                                                    isDisabled={!Helpers.isEmpty(item.rate)}
                                                                                />
                                                                            </td>
                                                                        </tr>
                                                                    ))
                                                                }
                                                            </tbody>
                                                            <tfoot>
                                                                <tr className='eskalasyon-toplam'>
                                                                    <td>TOPLAM ETKİ</td>
                                                                    <td></td>
                                                                    <td className='eskalasyon-toplam-deger'>
                                                                        {etkinOran > 0 ? '%' + Helpers.correctPrice(etkinOran, 2) : ''}
                                                                    </td>
                                                                    <td className='eskalasyon-toplam-deger'>
                                                                        {kalemTutarToplami > 0 ? Helpers.correctPrice(kalemTutarToplami, 2) : ''}
                                                                    </td>
                                                                </tr>
                                                            </tfoot>
                                                        </table>

                                                        <div className='text-muted' style={{ fontSize: 11.5 }}>
                                                            Oran girilen kalemde artış, kalemin ağırlığı kadar fiyata yansır
                                                            (fiyat × ağırlık × oran). Tutar girilen kalemde ağırlığa bakılmaz,
                                                            tutar doğrudan eklenir. Boş ya da sıfır satır fiyatı etkilemez.
                                                        </div>

                                                        {
                                                            kalemHatasi && (
                                                                <div className='text-danger' style={{ fontSize: 12 }}>
                                                                    Aynı satırda oran ve tutar birlikte girilemez.
                                                                </div>
                                                            )
                                                        }
                                                    </div>
                                                )
                                        }
                                    </div>
                                </div>
                            )
                        }

                        <div className='row mt-5'>
                            <div className='col-md-12'>
                                <CustomTextInput labelText='Eskalasyon Açıklaması' value={escalationDescription} setValue={setEscalationDescription} />
                            </div>
                        </div>

                        <Box className='ebbe-hr m-0 mt-5'>&nbsp;</Box>

                        <Box className='mt-5' flexDirection='row'>
                            <Box
                                title={eskalasyonGecerli ? undefined : 'Tarihleri ve oran/tutar alanlarını doldurun.'}
                                onClick={eskalasyonGecerli ? applyEscalation : undefined}
                                opacity={eskalasyonGecerli ? '1' : '0.5'}
                                className='actionIcon p-2 bg-success'
                                justifyContent='center'
                                flexDirection='row'
                                borderRadius='4px'
                            >
                                <Box mr='5px' justifyContent='center'><i className='text-white fa fa-check' /></Box>
                                <Box justifyContent='center'><span style={{ fontWeight: 600 }} className='text-white'>Güncelle</span></Box>
                            </Box>
                        </Box>

                        <div className='mt-4' style={{ fontSize: 12.5, color: '#1b5faa', background: '#f1faff', border: '1px solid #c7e3ff', borderRadius: 6, padding: '9px 12px' }}>
                            {
                                secililer.length === 0
                                    ? <span>Aşağıdan anlaşma seçilmedi: işlem <b>tüm anlaşmalara</b> uygulanacak ({data.totalRecordCount} kayıt).</span>
                                    : <span><b>{secililer.length}</b> anlaşma seçili: işlem <b>yalnız seçilenlere</b> uygulanacak.</span>
                            }
                            <br />
                            Güncelle'ye basıldığında işlem <b>kuyruğa</b> alınır ve bütün anlaşmalar tek işlemde güncellenir:
                            yarım kalmış fiyat olmaz. Her anlaşma için <b>yeni bir sürüm</b> oluşturulur; eski anlaşmanın bitiş
                            tarihi yeni başlangıçtan bir gün öncesine çekilir, geçmiş fiyatlar değişmez.
                            {!allCostItems && etkinOran > 0 ? ' Oranlardan gelen toplam etki: %' + Helpers.correctPrice(etkinOran, 2) + '.' : ''}
                        </div>
                    </KTCardBody>
                </KTCard>

                <KTCard className='ebbe-card'>
                    <KTCardBody className='py-3'>
                        <div className='d-flex justify-content-between align-items-center mb-3 flex-wrap' style={{ gap: 8 }}>
                            <h5 className='m-0 text-black'>Anlaşmalar</h5>

                            <div className='d-flex align-items-center' style={{ gap: 8 }}>
                                <button className='btn btn-sm btn-light-success' onClick={() => excelExport('selected')} disabled={secililer.length === 0}>Excel — Seçilenler</button>
                                <button className='btn btn-sm btn-light-success' onClick={() => excelExport('all')}>Excel — Tümü</button>
                                <button className='btn btn-sm btn-light-success' onClick={() => excelExport('active')}>Excel — Aktif</button>
                            </div>
                        </div>

                        {
                            sablonlar.length > 1 && (
                                <div className='mb-3'>
                                    <span className='text-muted me-2' style={{ fontSize: 12.5, fontWeight: 600 }}>Seçilen şablonlar:</span>
                                    {
                                        sablonlar.map(x => (
                                            <span key={x.schemeId} className='badge badge-light-primary me-2'>{x.moduleName} · {x.refNo}</span>
                                        ))
                                    }
                                </div>
                            )
                        }

                        <ColumnFilterChips
                            columns={kolonlar}
                            definitions={columnFilterDefinitions}
                            filters={filter.columnFilters}
                            onChange={changeColumnFilters}
                        />

                        <div className='table-responsive new-table-container'>
                            <table id='kt_table_users' className='table align-middle table-row-dashed dataTable no-footer ebbe-compact-table'>
                                <thead>
                                    <tr className='text-start text-muted fw-bolder text-uppercase'>
                                        <th style={{ width: 1 }}>
                                            <div className='d-flex'>
                                                <label className='form-check form-check-sm form-check-custom form-check-solid'>
                                                    <input onChange={selectAll} className='form-check-input' type='checkbox' checked={secililer.length > 0 && secililer.length == data.items.length} />
                                                </label>
                                            </div>
                                        </th>

                                        {
                                            kolonlar.map((item) =>
                                                <th key={item.name} {...thProps(item.name)}>{item.title}{sortArrow(item.name)}</th>
                                            )
                                        }
                                    </tr>

                                    <ColumnFilterRow
                                        columns={kolonlar}
                                        definitions={columnFilterDefinitions}
                                        filters={filter.columnFilters}
                                        onChange={changeColumnFilters}
                                        leadingCellCount={1}
                                    />
                                </thead>
                                <tbody className='text-gray-600 fw-bold'>
                                    {
                                        data.items.length == 0 && (
                                            <tr><td className='text-center' colSpan={99}>Kayıt bulunamadı.</td></tr>
                                        )
                                    }
                                    {
                                        data.items.map((item) =>
                                            <tr key={item.agreementId} style={{ background: item.isSelected ? 'rgba(204, 204, 204, 0.25)' : undefined }}>
                                                <td style={{ width: 1 }}>
                                                    <div className='d-flex'>
                                                        <label className='form-check form-check-sm form-check-custom form-check-solid'>
                                                            <input onChange={() => setItemSelected(item.agreementId)} className='form-check-input' type='checkbox' checked={item.isSelected} />
                                                        </label>
                                                    </div>
                                                </td>

                                                {
                                                    kolonlar.map((column) => (
                                                        <React.Fragment key={column.name}>{getColumnValue(item, column.name, column.className)}</React.Fragment>
                                                    ))
                                                }
                                            </tr>
                                        )
                                    }
                                </tbody>
                            </table>
                        </div>
                    </KTCardBody>

                    <TableFooter
                        changeTake={changeTake}
                        take={filter.take}
                        currentPage={filter.currentPage}
                        performPagination={performPagination}
                        totalPageCount={Math.ceil(data.totalRecordCount / filter.take)}
                        totalRecordCount={data.totalRecordCount}
                        selectedCount={secililer.length}
                    />
                </KTCard>

                <KTCard className='ebbe-card mt-5'>
                    <KTCardBody className='py-3'>
                        <div className='d-flex justify-content-between align-items-center'>
                            <h5 className='m-0 text-black'>Eskalasyon Geçmişi {history.length > 0 ? '(' + history.length + ')' : ''}</h5>
                            <button className='btn btn-sm btn-light' onClick={() => setHistoryOpen(!historyOpen)}>
                                {historyOpen ? 'Gizle' : 'Göster'}
                            </button>
                        </div>

                        {
                            historyOpen && (
                                history.length == 0
                                    ? <div className='text-muted text-center py-5'>Bu şablonlarda daha önce eskalasyon yapılmamış.</div>
                                    : (
                                        <div className='mt-3'>
                                            {
                                                history.map((h) => (
                                                    <div key={h.priceEscalationId} className='mb-5'>
                                                        <div className='fw-bolder text-black mb-2'>
                                                            {h.processDate} · {h.userName} · {h.startDateStr} – {h.endDateStr} · {h.ruleText}
                                                            <span className='text-muted fw-normal ms-2' style={{ fontSize: 12 }}>
                                                                {h.appliedCount} anlaşma güncellendi{h.skippedCount > 0 ? ', ' + h.skippedCount + ' atlandı' : ''}
                                                                {h.effectiveRate ? ' · toplam etki %' + Helpers.correctPrice(h.effectiveRate, 2) : ''}
                                                            </span>
                                                        </div>

                                                        {
                                                            h.costItems.length > 0 && (
                                                                <div className='table-responsive mb-2'>
                                                                    <table className='table align-middle table-row-dashed dataTable no-footer' id='kt_table_users'>
                                                                        <thead>
                                                                            <tr className='text-start text-muted fw-bolder text-uppercase'>
                                                                                <th>Maliyet Kalemi</th>
                                                                                <th className='custom-text-right'>Ağırlık</th>
                                                                                <th className='custom-text-right'>Girilen Oran</th>
                                                                                <th className='custom-text-right'>Girilen Tutar</th>
                                                                                <th className='custom-text-right'>Fiyata Etkisi</th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody className='text-gray-600 fw-bold'>
                                                                            {
                                                                                h.costItems.map((k, i) => (
                                                                                    <tr key={i}>
                                                                                        <td>{k.name}</td>
                                                                                        <td className='custom-text-right'>{k.weight ? '%' + Helpers.correctPrice(k.weight, 2) : ''}</td>
                                                                                        <td className='custom-text-right'>{k.rate ? '%' + Helpers.correctPrice(k.rate, 2) : ''}</td>
                                                                                        <td className='custom-text-right'>{k.amount ? Helpers.correctPrice(k.amount, 2) : ''}</td>
                                                                                        <td className='custom-text-right'>{k.effectRate ? '%' + Helpers.correctPrice(k.effectRate, 2) : ''}</td>
                                                                                    </tr>
                                                                                ))
                                                                            }
                                                                        </tbody>
                                                                    </table>
                                                                </div>
                                                            )
                                                        }

                                                        <div className='table-responsive'>
                                                            <table className='table align-middle table-row-dashed dataTable no-footer' id='kt_table_users'>
                                                                <thead>
                                                                    <tr className='text-start text-muted fw-bolder text-uppercase'>
                                                                        <th>Kaynak Anlaşma</th>
                                                                        <th>Yeni Anlaşma</th>
                                                                        <th className='custom-text-right'>Birim Fiyat Önce</th>
                                                                        <th className='custom-text-right'>Birim Fiyat Sonra</th>
                                                                        <th className='custom-text-right'>Artış</th>
                                                                        <th>Durum</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody className='text-gray-600 fw-bold'>
                                                                    {
                                                                        h.agreements.map((a, i) => (
                                                                            <tr key={i}>
                                                                                <td>{a.sourceRefNo}</td>
                                                                                <td>{a.newRefNo}</td>
                                                                                <td className='custom-text-right'>{Helpers.correctPrice(a.unitPriceBefore, 2)}</td>
                                                                                <td className='custom-text-right'>{Helpers.correctPrice(a.unitPriceAfter, 2)}</td>
                                                                                <td className='custom-text-right'>{a.increaseRate ? '%' + Helpers.correctPrice(a.increaseRate, 2) : ''}</td>
                                                                                <td className={a.isSkipped ? 'text-danger' : 'text-success'}>
                                                                                    {a.isSkipped ? (a.skipReason ?? 'Atlandı') : 'Güncellendi'}
                                                                                </td>
                                                                            </tr>
                                                                        ))
                                                                    }
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                ))
                                            }
                                        </div>
                                    )
                            )
                        }
                    </KTCardBody>
                </KTCard>
            </Content>
        </React.Fragment>
    )
}

export default BulkPriceDetail;
